import moment from 'moment';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { HD_CHART_TYPE_ICONS } from '../../library/hd-chart-type-metadata';
import { HdLifeTimelineMarker } from '../../timeline/hd-life-timeline.models';
import { hdMomentColorHex, parseHdMomentEmotions } from './hd-moment-fields';

/** Eén momenten-rij: de relatie + afgeleide weergavevelden (sectie én alle-momenten-dialog). */
export interface HdMomentRowVm {
  readonly relation: PlatformChartRelationDto;
  readonly icon: string;
  readonly name: string;
  readonly momentText: string | null;
  /** Momentkleur als hex (preset-key opgelost via het dusk-palet); null = geen kleur. */
  readonly colorHex: string | null;
  /**
   * Notitie aanwezig? De inhoud zelf toont in de lijsten bewust niet
   * (uitgebreidere notities horen in het Notities-paneel van de geopende
   * chart); de rij draagt enkel een indicator.
   */
  readonly hasNote: boolean;
  readonly attachmentCount: number;
}

export function buildHdMomentRow(relation: PlatformChartRelationDto): HdMomentRowVm {
  return {
    relation,
    // Custom chart-icoon wint van het type-icoon (beslispunt 7 notable moments).
    icon: relation.otherChartIcon
      || (HD_CHART_TYPE_ICONS[relation.otherChartType ?? PlatformChartType.Unknown]
        ?? HD_CHART_TYPE_ICONS[PlatformChartType.Unknown]),
    name: relation.otherChartName ?? '',
    momentText: relation.otherChartMomentUtc
      ? moment.utc(relation.otherChartMomentUtc).local().format('D MMM YYYY, HH:mm')
      : null,
    colorHex: hdMomentColorHex(relation.otherChartColor),
    hasNote: !!relation.note?.trim(),
    attachmentCount: relation.noteAttachments?.length ?? 0,
  };
}

/**
 * Momenten ván een chart: bewaarde snapshots (hasMoment), cycles/solar returns
 * (hasCycle) en gelinkte charts — chronologisch aflopend op het geflattende
 * sorteermoment (MomentUtc ?? CycleAnchorUtc ?? BirthDateUtc).
 */
export function buildHdMomentRows(relations: readonly PlatformChartRelationDto[]): HdMomentRowVm[] {
  return relations
    .filter(relation => relation.relationType === 'hasMoment' || relation.relationType === 'hasCycle')
    .map(relation => buildHdMomentRow(relation))
    .sort((a, b) => (b.relation.otherChartMomentUtc ?? '').localeCompare(a.relation.otherChartMomentUtc ?? ''));
}

/**
 * Momenten-rijen → tijdlijn-/kalendermarkers (gedeeld door de
 * levenstijdlijn-pagina en de sectie-kalender, spec levenstijdlijn §2.7).
 */
export function buildHdLifeTimelineMarkers(rows: readonly HdMomentRowVm[]): HdLifeTimelineMarker[] {
  return rows
    .map((row): HdLifeTimelineMarker => ({
      id: row.relation.id ?? row.relation.otherChartId ?? row.name,
      kind: row.relation.otherChartMomentEndUtc ? 'range' : 'moment',
      startUtc: row.relation.otherChartMomentUtc ?? '',
      endUtc: row.relation.otherChartMomentEndUtc ?? null,
      label: row.name,
      icon: row.icon,
      hasNote: row.hasNote,
      attachmentCount: row.attachmentCount,
      openChartId: row.relation.otherChartId ?? null,
      colorHex: row.colorHex,
      category: row.relation.otherChartCategory ?? null,
      mood: row.relation.otherChartMood ?? null,
      vitality: row.relation.otherChartVitality ?? null,
      emotions: parseHdMomentEmotions(row.relation.otherChartEmotions),
    }))
    .filter(marker => !!marker.startUtc);
}
